import React from "react";
import PropTypes from "prop-types";
import { Drawer, Icon, Empty } from "antd";
import { fetchVoiceAnalytics } from "services/case-service";

// LMS returns `chat` as a JSON-ENCODED STRING of turns, not an array:
//   "[{\"id\":0,\"start\":\"0:00:00\",\"end\":\"0:00:03\",\"text\":\"Hello.\",\"type\":\"Unknown\"}]"
// Parse defensively — a malformed/absent transcript must not blow up the drawer.
function parseChat(raw) {
    if (!raw) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

// Which side of the thread a turn sits on, from the vendor's own speaker label.
// Agent left, customer right. Returns null when the vendor did not identify the
// speaker (it sends type "Unknown" when diarisation failed).
function speakerSide(type) {
    const t = String(type || "").toLowerCase();
    if (t === "agent" || t === "employee" || t === "a") {
        return "agent";
    }
    if (t === "customer" || t === "borrower" || t === "b") {
        return "customer";
    }
    return null;
}

/**
 * Assigns every turn a side.
 *
 * When the vendor labelled the speakers we use those labels — they are fact.
 *
 * When it did not (all turns come back type "Unknown"), we INFER the split by
 * alternating on each turn, which is a heuristic, not data: back-to-back turns
 * from the same person will land on opposite sides. The thread is flagged
 * `inferred` so the UI can say so out loud — on a credit call, a reader must
 * never mistake a guessed attribution for a recorded one.
 */
function assignSides(turns) {
    const labelled = turns.some(t => speakerSide(t.type));
    if (labelled) {
        return {
            inferred: false,
            sides: turns.map(t => speakerSide(t.type) || "customer")
        };
    }
    return {
        inferred: true,
        // The agent opens an outbound call, so turn 0 is taken as the agent.
        sides: turns.map((t, i) => (i % 2 === 0 ? "agent" : "customer"))
    };
}

/**
 * Call transcript drawer — right-hand slider (same shape as the case-comments
 * drawer). AI call summary pinned at the top, the turn-by-turn transcript
 * rendered as a chat thread below it.
 */
class CallTranscriptDrawer extends React.Component {
    state = {
        loading: false,
        error: null,
        summary: null,
        turns: [],
        audioUrl: null
    };

    componentDidUpdate(prevProps) {
        const opened =
            this.props.visible &&
            (!prevProps.visible || prevProps.uniqueId !== this.props.uniqueId);
        if (opened) {
            this.load();
        }
    }

    load() {
        const { uniqueId } = this.props;
        if (!uniqueId) {
            this.setState({
                loading: false,
                error: "This call has no vendor call id, so no transcript can be fetched.",
                summary: null,
                turns: [],
                audioUrl: null
            });
            return;
        }
        this.setState({ loading: true, error: null });
        fetchVoiceAnalytics(uniqueId)
            .then(({ data }) => {
                // LMS answers with a LIST of records; take the first.
                const rows = Array.isArray(data) ? data : (data && data.results) || [];
                const record = rows[0];
                if (!record) {
                    this.setState({
                        loading: false,
                        summary: null,
                        turns: [],
                        audioUrl: null
                    });
                    return;
                }
                this.setState({
                    loading: false,
                    summary: record.call_summary || null,
                    turns: parseChat(record.chat),
                    audioUrl: record.file || record.call_recording || null
                });
            })
            .catch(() => {
                this.setState({
                    loading: false,
                    error: "Could not load the transcript for this call.",
                    summary: null,
                    turns: [],
                    audioUrl: null
                });
            });
    }

    // Skeleton shimmer while the transcript loads — reads as "content is coming"
    // rather than a bare spinner on an empty panel.
    renderSkeleton() {
        return (
            <div className={"cc-tx__skeleton"}>
                <div className={"cc-tx__sk-card"} />
                {[62, 84, 48, 76, 55].map((w, i) => (
                    <div
                        key={i}
                        className={
                            "cc-tx__sk-line" + (i % 2 ? " cc-tx__sk-line--alt" : "")
                        }
                        style={{ width: `${w}%` }}
                    />
                ))}
            </div>
        );
    }

    renderBody() {
        const { loading, error, summary, turns, audioUrl } = this.state;
        if (loading) {
            return this.renderSkeleton();
        }
        if (error) {
            return (
                <div className={"cc-tx__center"}>
                    <Empty description={error} />
                </div>
            );
        }
        if (!summary && !turns.length) {
            return (
                <div className={"cc-tx__center"}>
                    <Empty description={"No transcript available for this call"} />
                </div>
            );
        }
        // Call length = the last turn's end stamp ("0:06:12").
        const duration = turns.length ? turns[turns.length - 1].end : null;
        const { sides, inferred } = assignSides(turns);
        return (
            <React.Fragment>
                {summary ? (
                    <div className={"cc-tx__summary"}>
                        <div className={"cc-tx__summary-head"}>
                            <span className={"cc-tx__ai"}>
                                <Icon type={"thunderbolt"} theme={"filled"} />
                                AI
                            </span>
                            Call summary
                        </div>
                        <p className={"cc-tx__summary-text"}>{summary}</p>
                    </div>
                ) : null}

                {audioUrl ? (
                    <div className={"cc-tx__player"}>
                        <audio
                            className={"cc-tx__audio"}
                            controls
                            preload={"none"}
                            src={audioUrl}
                        />
                    </div>
                ) : null}

                {turns.length ? (
                    <React.Fragment>
                        <div className={"cc-tx__thread-head"}>
                            <span className={"cc-tx__thread-title"}>
                                Transcript
                            </span>
                            <span className={"cc-tx__meta"}>
                                <span className={"cc-tx__pill"}>
                                    <Icon type={"message"} /> {turns.length} turns
                                </span>
                                {duration ? (
                                    <span className={"cc-tx__pill"}>
                                        <Icon type={"clock-circle"} /> {duration}
                                    </span>
                                ) : null}
                            </span>
                        </div>
                        {inferred ? (
                            <div className={"cc-tx__notice"}>
                                <Icon type={"info-circle"} />
                                <span>
                                    The vendor did not label the speakers on this
                                    call. Sides are <b>inferred from turn order</b>{" "}
                                    — treat the Agent/Customer split as indicative,
                                    not verified.
                                </span>
                            </div>
                        ) : null}
                        <div className={"cc-tx__thread"}>
                            {turns.map((turn, index) => {
                                const side = sides[index];
                                return (
                                    <div
                                        key={turn.id != null ? turn.id : index}
                                        className={
                                            "cc-tx__turn cc-tx__turn--" + side
                                        }
                                    >
                                        <span
                                            className={
                                                "cc-tx__who cc-tx__who--" + side
                                            }
                                            title={
                                                side === "agent"
                                                    ? "Agent"
                                                    : "Customer"
                                            }
                                        >
                                            {side === "agent" ? "A" : "C"}
                                        </span>
                                        <div className={"cc-tx__bubble"}>
                                            <div className={"cc-tx__text"}>
                                                {turn.text}
                                            </div>
                                            {turn.start ? (
                                                <div className={"cc-tx__time"}>
                                                    {turn.start}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </React.Fragment>
                ) : (
                    <div className={"cc-tx__center"}>
                        <Empty
                            description={"Summary only — no transcript on file"}
                        />
                    </div>
                )}
            </React.Fragment>
        );
    }

    render() {
        const { visible, onClose, subtitle } = this.props;
        return (
            <Drawer
                className={"cc-transcript-drawer"}
                title={null}
                width={520}
                placement={"right"}
                closable={false}
                onClose={onClose}
                visible={visible}
                destroyOnClose
            >
                <div className={"cc-tx__header"}>
                    <span className={"cc-tx__avatar"}>
                        <Icon type={"phone"} theme={"filled"} />
                    </span>
                    <div className={"cc-tx__headings"}>
                        <div className={"cc-tx__title"}>Call transcript</div>
                        {subtitle ? (
                            <div className={"cc-tx__subtitle"}>{subtitle}</div>
                        ) : null}
                    </div>
                    <button
                        type={"button"}
                        className={"cc-tx__close"}
                        onClick={onClose}
                        aria-label={"Close"}
                    >
                        <Icon type={"close"} />
                    </button>
                </div>
                <div className={"cc-tx__body"}>{this.renderBody()}</div>
            </Drawer>
        );
    }
}

CallTranscriptDrawer.propTypes = {
    visible: PropTypes.bool,
    onClose: PropTypes.func,
    // Chitthi CDR `vendor_call_id` — LMS's `unique_id`.
    uniqueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subtitle: PropTypes.string
};

export default CallTranscriptDrawer;
